import * as Sentry from '@sentry/react';
import ReactGA from 'react-ga4';
import { onCLS, onINP, onFCP, onLCP, onTTFB } from 'web-vitals';

// Environment configuration
const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

// Configuration interfaces
interface MonitoringConfig {
  sentryDsn?: string;
  googleAnalyticsId?: string;
  enableConsoleErrors?: boolean;
  enablePerformanceMonitoring?: boolean;
  enableAnalytics?: boolean;
}

interface ErrorInfo {
  message: string;
  stack?: string;
  componentStack?: string;
  userId?: string;
  userAgent?: string;
  url?: string;
  timestamp?: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
  custom_parameters?: Record<string, unknown>;
}

interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

class MonitoringService {
  private initialized = false;
  private config: MonitoringConfig = {};
  private performanceMetrics: PerformanceMetric[] = [];

  /**
   * Initialize monitoring services
   */
  init(config: MonitoringConfig = {}): void {
    if (this.initialized) {
      console.warn('Monitoring service already initialized');
      return;
    }

    this.config = {
      enableConsoleErrors: true,
      enablePerformanceMonitoring: true,
      enableAnalytics: true,
      ...config,
    };

    // Initialize Sentry for error tracking
    if (this.config.sentryDsn && isProduction) {
      this.initSentry();
    }

    // Initialize Google Analytics
    if (this.config.googleAnalyticsId && this.config.enableAnalytics) {
      this.initGoogleAnalytics();
    }

    // Initialize performance monitoring
    if (this.config.enablePerformanceMonitoring) {
      this.initPerformanceMonitoring();
    }

    // Setup global error handlers
    this.setupGlobalErrorHandlers();

    this.initialized = true;
    console.log('Monitoring service initialized', { config: this.config });
  }

  /**
   * Initialize Sentry error tracking
   */
  private initSentry(): void {
    Sentry.init({
      dsn: this.config.sentryDsn,
      environment: import.meta.env.MODE,
      tracesSampleRate: isProduction ? 0.1 : 1.0,
      beforeSend: (event, hint) => {
        // Filter out development errors
        if (isDevelopment && event.exception) {
          console.group('🐛 Sentry Error (Development)');
          console.error(hint.originalException || hint.syntheticException);
          console.groupEnd();
          return null; // Don't send to Sentry in development
        }
        return event;
      },
    });

    console.log('✅ Sentry initialized');
  }

  /**
   * Initialize Google Analytics
   */
  private initGoogleAnalytics(): void {
    ReactGA.initialize(this.config.googleAnalyticsId!, {
      testMode: isDevelopment,
      gaOptions: {
        anonymize_ip: true,
      },
    });

    // Track initial page view
    this.trackPageView(window.location.pathname + window.location.search);
    console.log('✅ Google Analytics initialized');
  }

  /**
   * Initialize Web Vitals performance monitoring
   */
  private initPerformanceMonitoring(): void {
    // Core Web Vitals
    onCLS(this.handlePerformanceMetric.bind(this));
    onINP(this.handlePerformanceMetric.bind(this));
    onFCP(this.handlePerformanceMetric.bind(this));
    onLCP(this.handlePerformanceMetric.bind(this));
    onTTFB(this.handlePerformanceMetric.bind(this));

    console.log('✅ Performance monitoring initialized');
  }

  /**
   * Handle performance metrics
   */
  private handlePerformanceMetric(metric: PerformanceMetric): void {
    this.performanceMetrics.push(metric);

    // Log in development
    if (isDevelopment) {
      console.log(`📊 ${metric.name}:`, {
        value: metric.value,
        rating: metric.rating,
      });
    }

    // Send to analytics
    if (this.config.enableAnalytics) {
      this.trackEvent({
        action: 'web_vital',
        category: 'Performance',
        label: metric.name,
        value: Math.round(metric.value),
        custom_parameters: {
          metric_rating: metric.rating,
          metric_delta: metric.delta,
        },
      });
    }

    // Send critical performance issues to Sentry
    if (metric.rating === 'poor' && isProduction) {
      Sentry.addBreadcrumb({
        message: `Poor ${metric.name} performance`,
        level: 'warning',
        data: {
          value: metric.value,
          rating: metric.rating,
        },
      });
    }
  }

  /**
   * Setup global error handlers
   */
  private setupGlobalErrorHandlers(): void {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', event => {
      this.captureError({
        message: `Unhandled Promise Rejection: ${event.reason}`,
        severity: 'high',
        tags: { type: 'unhandled_rejection' },
        extra: { reason: event.reason },
      });
    });

    // Handle global errors
    window.addEventListener('error', event => {
      this.captureError({
        message: event.message,
        stack: event.error?.stack,
        severity: 'high',
        tags: { type: 'global_error' },
        extra: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    });

    // Override console.error in development (but avoid infinite loops)
    if (isDevelopment && this.config.enableConsoleErrors) {
      const originalConsoleError = console.error;
      console.error = (...args) => {
        originalConsoleError(...args);
        // Only capture to Sentry in production to avoid infinite loops
        if (isProduction && this.config.sentryDsn) {
          Sentry.captureException(new Error(args.join(' ')));
        }
      };
    }
  }

  /**
   * Capture and report errors
   */
  captureError(errorInfo: ErrorInfo): void {
    const enrichedError = {
      ...errorInfo,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    // Log in development (avoid infinite loop with console.error override)
    if (isDevelopment) {
      console.group(`🚨 Error Captured (${errorInfo.severity || 'medium'})`);
      console.warn(enrichedError.message); // Use console.warn instead of console.error
      if (enrichedError.stack) console.warn(enrichedError.stack);
      console.log('Error details:', enrichedError);
      console.groupEnd();
    }

    // Send to Sentry in production
    if (isProduction && this.config.sentryDsn) {
      Sentry.withScope(scope => {
        scope.setLevel(this.getSentryLevel(errorInfo.severity));

        if (errorInfo.userId) {
          scope.setUser({ id: errorInfo.userId });
        }

        if (errorInfo.tags) {
          Object.entries(errorInfo.tags).forEach(([key, value]) => {
            scope.setTag(key, value);
          });
        }

        if (errorInfo.extra) {
          scope.setContext('extra', errorInfo.extra);
        }

        Sentry.captureException(new Error(errorInfo.message));
      });
    }

    // Track error in analytics
    if (this.config.enableAnalytics) {
      this.trackEvent({
        action: 'error',
        category: 'Error',
        label: errorInfo.message,
        custom_parameters: {
          error_severity: errorInfo.severity || 'medium',
          error_type: errorInfo.tags?.type || 'unknown',
        },
      });
    }
  }

  /**
   * Capture a message for logging/monitoring
   */
  captureMessage(
    message: string,
    level: 'info' | 'warning' | 'error' = 'info',
    data?: Record<string, unknown>
  ): void {
    // Log in development
    if (isDevelopment) {
      console.log(`📝 Message Captured (${level}):`, message, data);
    }

    // Send to Sentry in production
    if (isProduction && this.config.sentryDsn) {
      Sentry.withScope(scope => {
        scope.setLevel(level);
        if (data) {
          scope.setContext('data', data);
        }
        Sentry.captureMessage(message);
      });
    }

    // Track in analytics
    if (this.config.enableAnalytics) {
      this.trackEvent({
        action: 'message',
        category: 'Info',
        label: message,
        custom_parameters: {
          message_level: level,
          ...data,
        },
      });
    }
  }

  /**
   * Capture an exception for monitoring
   */
  captureException(error: unknown, context?: Record<string, unknown>): void {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;

    // Log in development
    if (isDevelopment) {
      console.group('🚨 Exception Captured');
      console.error(error);
      if (context) console.log('Context:', context);
      console.groupEnd();
    }

    // Send to Sentry in production
    if (isProduction && this.config.sentryDsn) {
      Sentry.withScope(scope => {
        if (context) {
          scope.setContext('exception_context', context);
        }

        if (error instanceof Error) {
          Sentry.captureException(error);
        } else {
          Sentry.captureMessage(`Non-Error exception: ${String(error)}`);
        }
      });
    }

    // Track in analytics
    if (this.config.enableAnalytics) {
      this.trackEvent({
        action: 'exception',
        category: 'Error',
        label: errorMessage,
        custom_parameters: {
          has_stack: !!errorStack,
          error_type: typeof error,
          ...context,
        },
      });
    }
  }

  /**
   * Convert severity to Sentry level
   */
  private getSentryLevel(severity?: string): Sentry.SeverityLevel {
    switch (severity) {
      case 'low':
        return 'info';
      case 'medium':
        return 'warning';
      case 'high':
        return 'error';
      case 'critical':
        return 'fatal';
      default:
        return 'error';
    }
  }

  /**
   * Track page views
   */
  trackPageView(path: string, title?: string): void {
    if (!this.config.enableAnalytics) return;

    if (isDevelopment) {
      console.log('📄 Page view:', { path, title });
    }

    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title,
    });
  }

  /**
   * Track custom events
   */
  trackEvent(event: AnalyticsEvent): void {
    if (!this.config.enableAnalytics) return;

    if (isDevelopment) {
      console.log('📊 Event tracked:', event);
    }

    ReactGA.event(event.action, {
      event_category: event.category,
      event_label: event.label,
      value: event.value,
      ...event.custom_parameters,
    });
  }

  /**
   * Track user interactions
   */
  trackUserInteraction(
    element: string,
    action: string,
    additionalData?: Record<string, unknown>
  ): void {
    this.trackEvent({
      action: action,
      category: 'User Interaction',
      label: element,
      custom_parameters: additionalData,
    });
  }

  /**
   * Set user context for error tracking
   */
  setUser(userId: string, email?: string, username?: string): void {
    if (isProduction && this.config.sentryDsn) {
      Sentry.setUser({
        id: userId,
        email,
        username,
      });
    }

    if (this.config.enableAnalytics) {
      ReactGA.set({ user_id: userId });
    }

    if (isDevelopment) {
      console.log('👤 User context set:', { userId, email, username });
    }
  }

  /**
   * Add breadcrumb for debugging
   */
  addBreadcrumb(
    message: string,
    category: string,
    data?: Record<string, unknown>
  ): void {
    if (isProduction && this.config.sentryDsn) {
      Sentry.addBreadcrumb({
        message,
        category,
        data,
        timestamp: Date.now() / 1000,
      });
    }

    if (isDevelopment) {
      console.log('🍞 Breadcrumb:', { message, category, data });
    }
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics(): PerformanceMetric[] {
    return [...this.performanceMetrics];
  }

  /**
   * Clear performance metrics
   */
  clearPerformanceMetrics(): void {
    this.performanceMetrics = [];
  }

  /**
   * Test error reporting (development only)
   */
  testErrorReporting(): void {
    if (!isDevelopment) {
      console.warn('Error testing is only available in development mode');
      return;
    }

    console.log('🧪 Testing error reporting...');

    this.captureError({
      message: 'Test error from monitoring service',
      severity: 'low',
      tags: { type: 'test' },
      extra: { testData: 'This is a test error' },
    });
  }

  /**
   * Test analytics (development only)
   */
  testAnalytics(): void {
    if (!isDevelopment) {
      console.warn('Analytics testing is only available in development mode');
      return;
    }

    console.log('🧪 Testing analytics...');

    this.trackEvent({
      action: 'test_event',
      category: 'Test',
      label: 'Monitoring Service Test',
      value: 1,
      custom_parameters: {
        test_timestamp: new Date().toISOString(),
      },
    });
  }
}

// Create singleton instance
export const monitoring = new MonitoringService();

// Export types
export type { MonitoringConfig, ErrorInfo, AnalyticsEvent, PerformanceMetric };

// Export default
export default monitoring;
